import React, { useMemo, useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SchoolIcon from '@mui/icons-material/School';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';
import useFetchDatav2 from '../hooks/useFetchDatav2';
import MainContent from '../components/MainContent';
import Modal from '../components/Modal';
import Spinner from '../components/spinner/Spinner';

const statusColor = (status) => {
  switch ((status || '').toUpperCase()) {
    case 'ACTIVE':
    case 'WELCOMED':
    case 'ORIENTED':
      return 'bg-green-100 text-green-800';
    case 'COMPLETED':
      return 'bg-blue-100 text-blue-800';
    case 'WITHDRAWN':
      return 'bg-gray-200 text-gray-700';
    default:
      return 'bg-yellow-100 text-yellow-800';
  }
};

function SuperStudentDetail() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [selectedCohortId, setSelectedCohortId] = useState('');
  const [setAsPrimary, setSetAsPrimary] = useState(false);

  const { data, isLoading } = useFetchDatav2(
    ['super-student-detail', studentId],
    `/super-students/${studentId}`,
    {},
    'Could not load super student',
    !!studentId,
    true
  );

  const { data: cohortsData } = useFetchDatav2(
    ['cohorts-for-enroll'],
    `/cohorts?limit=200`,
    {},
    '',
    true,
    true
  );

  const student = data?.data?.student;
  const memberships = data?.data?.memberships ?? [];
  const pendingInvites = data?.data?.pending_invites ?? [];

  const availableCohorts = useMemo(() => {
    const membershipRows = data?.data?.memberships ?? [];
    const cohortRows = cohortsData?.data?.cohorts ?? [];
    const inMemberships = new Set(membershipRows.map((m) => m.cohort_id));
    return cohortRows.filter((c) => !inMemberships.has(c.id));
  }, [data, cohortsData]);

  const enrollMutation = useMutation({
    mutationFn: async ({ cohort_id, set_as_primary }) => {
      const res = await axiosInstancev2.post(
        `/super-students/${studentId}/enroll`,
        { student_id: studentId, cohort_id, set_as_primary }
      );
      return res.data;
    },
    onSuccess: () => {
      toast.success('Enrolled in cohort — welcome email dispatched');
      setShowEnrollModal(false);
      setSelectedCohortId('');
      setSetAsPrimary(false);
      queryClient.invalidateQueries(['super-student-detail', studentId]);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.error || 'Could not enroll');
    },
  });

  const handleEnrollSubmit = (e) => {
    e.preventDefault();
    if (!selectedCohortId) {
      toast.error('Pick a cohort');
      return;
    }
    enrollMutation.mutate({
      cohort_id: selectedCohortId,
      set_as_primary: setAsPrimary,
    });
  };

  if (isLoading) {
    return (
      <MainContent full>
        <div className="flex justify-center py-20">
          <Spinner />
        </div>
      </MainContent>
    );
  }

  if (!student) {
    return (
      <MainContent full>
        <div className="text-center py-20 text-gray-500">
          Super student not found.
        </div>
      </MainContent>
    );
  }

  return (
    <>
      <MainContent full>
        <div className="flex flex-col gap-5">
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
          >
            <NavLink to="/" className="flex items-center gap-1">
              <HomeIcon fontSize="small" /> Home
            </NavLink>
            <NavLink to="/super-students">Super Students</NavLink>
            <span className="text-[#8b173b] font-semibold">
              {student.firstname} {student.lastname}
            </span>
          </Breadcrumbs>

          <div className="bg-white rounded-md shadow-sm p-5 flex items-start justify-between flex-wrap gap-4">
            <div className="flex items-start gap-4">
              {student.profile_image ? (
                <img
                  src={student.profile_image}
                  alt=""
                  className="w-16 h-16 rounded-full object-cover border"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-[#8b173b] text-white text-2xl font-bold flex items-center justify-center">
                  {(student.firstname || '?').charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <h1 className="text-xl font-semibold capitalize">
                  {student.firstname} {student.lastname}
                </h1>
                <p className="text-sm text-gray-600">{student.email}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="font-mono text-xs bg-[#fff5f9] text-[#8b173b] px-2 py-0.5 rounded">
                    {student.super_student_id}
                  </span>
                  <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs font-medium">
                    Super Student
                  </span>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowEnrollModal(true)}
              className="bg-[#8b173b] text-white px-4 py-2 rounded-md flex items-center gap-2 hover:bg-[#701230]"
              disabled={availableCohorts.length === 0}
            >
              <AddCircleOutlineIcon fontSize="small" />
              Enroll in cohort
            </button>
          </div>

          <section>
            <h2 className="font-semibold mb-3 flex items-center gap-2">
              <SchoolIcon fontSize="small" /> Active memberships (
              {memberships.length})
            </h2>
            {memberships.length === 0 ? (
              <div className="bg-white rounded-md shadow-sm p-6 text-center text-gray-500">
                No memberships yet. Click &quot;Enroll in cohort&quot; to add
                one.
              </div>
            ) : (
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {memberships.map((m) => {
                  const primary = m.cohort?.primary_color || '#8b173b';
                  return (
                    <div
                      key={m.id}
                      className="bg-white rounded-md shadow-sm p-4 flex flex-col gap-2 border-l-4"
                      style={{ borderLeftColor: primary }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {m.cohort?.logo ? (
                            <img
                              src={m.cohort.logo}
                              alt=""
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div
                              className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white"
                              style={{ backgroundColor: primary }}
                            >
                              {(m.cohort?.name || 'C').charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <div className="font-semibold text-sm">
                              {m.cohort?.name || 'Cohort'}
                            </div>
                            <div className="text-[11px] text-gray-500 uppercase tracking-wider">
                              {m.cohort?.status || '—'}
                            </div>
                          </div>
                        </div>
                        {m.is_primary ? (
                          <span className="text-[10px] font-bold text-[#00999e] uppercase tracking-wider">
                            Primary
                          </span>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap gap-2 mt-1">
                        <span
                          className={`text-xs px-2 py-0.5 rounded font-medium ${statusColor(
                            m.onboarding_status
                          )}`}
                        >
                          {m.onboarding_status || 'PENDING'}
                        </span>
                        <span className="text-xs text-gray-600">
                          Welcome:{' '}
                          <strong>{m.welcome_email_status || 'PENDING'}</strong>
                        </span>
                      </div>
                      {m.joined_at ? (
                        <div className="text-[11px] text-gray-500">
                          Joined{' '}
                          {new Date(m.joined_at).toLocaleDateString('en-us', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {pendingInvites.length > 0 ? (
            <section>
              <h2 className="font-semibold mb-3">
                Pending cohort invites ({pendingInvites.length})
              </h2>
              <div className="bg-white rounded-md shadow-sm p-4 flex flex-col gap-2">
                {pendingInvites.map((inv) => (
                  <div
                    key={inv.id}
                    className="flex items-center justify-between border-b last:border-0 pb-2 last:pb-0"
                  >
                    <div>
                      <div className="font-medium text-sm">
                        {inv.cohort?.name || inv.cohort_id}
                      </div>
                      <div className="text-xs text-gray-500">
                        Invited{' '}
                        {inv.invited_at
                          ? new Date(inv.invited_at).toLocaleDateString()
                          : '—'}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="text-xs text-[#8b173b] underline"
                      onClick={() =>
                        navigate(`/cohort-detail/${inv.cohort_id}`)
                      }
                    >
                      View cohort
                    </button>
                  </div>
                ))}
              </div>
            </section>
          ) : null}
        </div>
      </MainContent>
      <Modal
        modalOpen={showEnrollModal}
        onClose={() => setShowEnrollModal(false)}
        title="Enroll in a cohort"
        small
      >
        <p className="text-xs text-gray-600 mb-4">
          The student will receive a welcome email for this cohort with their
          Super Student ID and orientation details.
        </p>
        <form onSubmit={handleEnrollSubmit} className="flex flex-col gap-3">
          <div>
            <label
              htmlFor="ss-enroll-cohort"
              className="text-xs font-medium text-gray-700 block mb-1"
            >
              Cohort
            </label>
            <select
              id="ss-enroll-cohort"
              value={selectedCohortId}
              onChange={(e) => setSelectedCohortId(e.target.value)}
              className="border rounded-md px-3 py-2 w-full"
              required
            >
              <option value="">Pick a cohort…</option>
              {availableCohorts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={setAsPrimary}
              onChange={(e) => setSetAsPrimary(e.target.checked)}
            />
            Set as primary cohort
          </label>
          <div className="flex gap-2 justify-end mt-2">
            <button
              type="button"
              onClick={() => setShowEnrollModal(false)}
              className="px-4 py-2 border rounded-md"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={enrollMutation.isLoading}
              className="bg-[#8b173b] text-white px-4 py-2 rounded-md disabled:opacity-60"
            >
              {enrollMutation.isLoading ? 'Enrolling...' : 'Enroll'}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}

export default SuperStudentDetail;
